import { Suspense, useLayoutEffect } from "react";
import { Outlet, useLocation, useNavigationType } from "react-router-dom";

import { ErrorBoundary } from "./ErrorBoundary";
import { OfflineBanner, PageSkeleton, PendingBar } from "./ui";

/**
 * The page slot shared by the venue and operator shells.
 *
 * The boundaries are keyed by path (the error boundary carries the key, so
 * the Suspense inside it is new on every navigation too): React Router wraps navigations in a transition, and a transition
 * keeps old content in an *existing* boundary while a new one shows its
 * fallback at once. Without the key a first visit to a tab would show nothing
 * until its chunk arrived.
 *
 * A new page starts at the top. BrowserRouter has no scroll restoration, so
 * opening a pass from a list scrolled to row 40 used to land the detail page
 * half-way down, past its header and actions. Back and forward (POP) are left
 * to the browser, which already returns the list to where it was.
 */
export function PanelOutlet() {
  const { pathname } = useLocation();
  const type = useNavigationType();

  useLayoutEffect(() => {
    if (type !== "POP") window.scrollTo(0, 0);
  }, [pathname, type]);

  return (
    <>
      <PendingBar />
      <OfflineBanner />
      <ErrorBoundary key={pathname}>
        <Suspense fallback={<PageSkeleton />}>
          <Outlet />
        </Suspense>
      </ErrorBoundary>
    </>
  );
}
